import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import {
  DiscontinuedCategoriesGQL,
  DiscontinuedCategoriesQuery,
  DiscontinueProductsGQL,
  DiscontinueProductsQuery,
  RegisterDiscontinuedCategoryGQL,
  RegisterDiscontinuedCategoryMutation,
  RegisterDiscontinuedJansGQL,
  RegisterDiscontinuedJansMutation,
  DeleteDiscontinuedProductMutation,
  SearchDiscontinuedProductInput,
  UpsertDiscontinuedMemoGQL,
  UpsertDiscontinuedMemoMutation,
  DeleteDiscontinuedProductGQL,
} from 'src/gql/generated';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { GraphQLFormattedError } from 'graphql';
import { gqlErrors } from 'src/app/utils/gql-errors';

@Injectable({
  providedIn: 'root',
})
export class DiscontinueService {
  constructor(
    private http: HttpService,
    private categoryGQL: DiscontinuedCategoriesGQL,
    private productsGQL: DiscontinueProductsGQL,
    private registerCategoryGQL: RegisterDiscontinuedCategoryGQL,
    private registerJansGQL: RegisterDiscontinuedJansGQL,
    private upsertMemoGQL: UpsertDiscontinuedMemoGQL,
    private deleteGQL: DeleteDiscontinuedProductGQL,
  ) {}

  public async fetchProducts(input: SearchDiscontinuedProductInput): Promise<DiscontinueProductsQuery | null> {
    const variables = {
      input,
    };
    const resp = await firstValueFrom(this.productsGQL.fetch({ variables: variables }));

    if (!gqlErrors(resp)) {
      return resp.data;
    }

    return this.handleError(gqlErrors(resp));
  }

  public async fetchCategories(): Promise<DiscontinuedCategoriesQuery | null> {
    const resp = await firstValueFrom(this.categoryGQL.fetch());

    if (!gqlErrors(resp)) {
      return resp.data;
    }
    return this.handleError(gqlErrors(resp));
  }

  public async registerCategoryName(name: string): Promise<RegisterDiscontinuedCategoryMutation | null> {
    const resp = await firstValueFrom(this.registerCategoryGQL.mutate({ variables: { name } }));

    if (!gqlErrors(resp)) {
      return resp.data;
    }
    return this.handleError(gqlErrors(resp));
  }

  public async upsertMemo(tmpId: number, memo: string): Promise<UpsertDiscontinuedMemoMutation | null> {
    const id = tmpId.toString();
    const resp = await firstValueFrom(this.upsertMemoGQL.mutate({ variables: { id, memo } }));

    if (!gqlErrors(resp)) {
      return resp.data;
    }
    return this.handleError(gqlErrors(resp));
  }

  public async registerJans(jans: string[], categoryId: number): Promise<RegisterDiscontinuedJansMutation | null> {
    const resp = await firstValueFrom(this.registerJansGQL.mutate({ variables: { jans, categoryId } }));

    if (!gqlErrors(resp)) {
      return resp.data;
    }
    return this.handleError(gqlErrors(resp));
  }

  public async deleteProduct(id: string): Promise<DeleteDiscontinuedProductMutation | null> {
    const resp = await firstValueFrom(this.deleteGQL.mutate({ variables: { id } }));

    if (!gqlErrors(resp)) {
      return resp.data;
    }
    return this.handleError(gqlErrors(resp));
  }

  private handleError(errors?: readonly GraphQLFormattedError[]) {
    if (this.http.hasExtensions(errors)) {
      this.http.handleError(errors[0].extensions);
    } else {
      this.http.handleError();
    }
    return null;
  }
}
